import type { ConversationAccessSummary } from '../types';

export const CONVERSATION_CAPABILITIES = {
  read: 'conversation:read',
  respond: 'conversation:respond',
  manage: 'conversation:manage',
  workspaceRun: 'workspace:run',
  terminalAttach: 'terminal:attach',
  artifactsRead: 'artifacts:read',
  artifactsPublish: 'artifacts:publish',
  secretsManage: 'secrets:manage',
  modelsUse: 'models:use',
  executorUse: 'executor:use',
  executorManage: 'executor:manage',
  codeWrite: 'code:write',
  codeRead: 'code:read',
  deployRun: 'deploy:run',
  releasePropose: 'release:propose',
  releaseRun: 'release:run'
} as const;

export function hasConversationCapability(
  access: ConversationAccessSummary | null | undefined,
  capability: string
): boolean {
  return Boolean(access?.admin_bypass || access?.capabilities.includes(capability));
}

/**
 * The page deliberately starts fail-closed while access is loading or after a
 * 403. The backend remains authoritative, but this prevents a stale page from
 * advertising controls that the current membership no longer grants.
 */
export function conversationControlAccess(access: ConversationAccessSummary | null | undefined) {
  const canRunWorkspace =
    hasConversationCapability(access, CONVERSATION_CAPABILITIES.workspaceRun) &&
    hasConversationCapability(access, CONVERSATION_CAPABILITIES.modelsUse);
  const canRespond =
    hasConversationCapability(access, CONVERSATION_CAPABILITIES.respond) &&
    canRunWorkspace;
  const canAttachTerminal = hasConversationCapability(
    access,
    CONVERSATION_CAPABILITIES.terminalAttach
  );
  return {
    canReadArtifacts: hasConversationCapability(access, CONVERSATION_CAPABILITIES.artifactsRead),
    canPublishArtifacts: hasConversationCapability(
      access,
      CONVERSATION_CAPABILITIES.artifactsPublish
    ),
    canRespond,
    canManageConversation: hasConversationCapability(access, CONVERSATION_CAPABILITIES.manage),
    canRunWorkspace,
    canAttachTerminal,
    canStartTerminalSessions:
      canAttachTerminal &&
      canRunWorkspace &&
      hasConversationCapability(access, CONVERSATION_CAPABILITIES.executorUse),
    canManageSecrets: hasConversationCapability(access, CONVERSATION_CAPABILITIES.secretsManage),
    canManageTools: hasConversationCapability(access, CONVERSATION_CAPABILITIES.executorManage),
    canReadCode: hasConversationCapability(access, CONVERSATION_CAPABILITIES.codeRead),
    canProposeCodeChanges:
      hasConversationCapability(access, CONVERSATION_CAPABILITIES.codeWrite) &&
      hasConversationCapability(access, CONVERSATION_CAPABILITIES.releasePropose),
    canMergeCodeChanges:
      hasConversationCapability(access, CONVERSATION_CAPABILITIES.codeWrite) &&
      hasConversationCapability(access, CONVERSATION_CAPABILITIES.releaseRun),
    canExecutePrivilegedOperations:
      hasConversationCapability(access, CONVERSATION_CAPABILITIES.deployRun) ||
      hasConversationCapability(access, CONVERSATION_CAPABILITIES.releaseRun)
  };
}
